import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// `ButtonShadcn` is the one that carries `variant` and `size`. The plain
// `Button` is a bare <button> wrapper, so both props were being handed
// straight to the DOM — no outline styling, and a React warning for `variant`.
import { ButtonShadcn as Button } from "@/components/ui/Button";
import Select from "@/components/ui/Select";
import { cn } from "@/lib/utils";

// Rows per page. The values the tables offer, and the options the control
// renders — one list, so they cannot drift apart.
const PAGE_SIZES = [5, 10, 25, 50].map((n) => ({
  label: String(n),
  value: n,
}));

/**
 * Functional component for pagination
 * @param currentPage: Current Page Number
 * @param totalItems: Total number of items to paginate
 * @param pageSize: Number of items per page(Default: 10)
 * @param onPageChange: Callback function when a page is selected
 * @param maxPageButtons: Maximum number of page buttons to display (Default: 5)
 * @param label: label of given pagination
 */
const Pagination = ({ currentPage, totalItems, pageSize = 10, onPageChange, label = "" }) => {
  const totalPages = Math.ceil(totalItems / pageSize);

  const handlePrev = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const start = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;

  const end = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="flex items-center justify-between gap-3">
      {/* Showing text - exactly like image */}
      <span className="hidden text-sm text-text-secondary md:inline">{totalItems === 0 ? `No ${label}` : `Showing ${start}-${end} of ${totalItems} ${label}`}</span>

      {/* Navigation buttons */}
      <div className="flex items-center gap-1.5">
        <Button
          type="button"
          variant="outline"
          disabled={currentPage === 1 || totalPages === 0}
          onClick={handlePrev}
          aria-label="Previous page"
          title="Previous page"
          className="
            !size-8
            !min-h-0
            !min-w-0
            !rounded-full
            !p-0
            flex
            shrink-0
            items-center
            justify-center
            border-border-primary
            bg-background-secondary
            text-text-secondary
            shadow-none
            transition-colors
            hover:bg-background-tertiary
            hover:text-text-primary
            disabled:pointer-events-none
            disabled:opacity-35
          "
        >
          <ChevronLeft size={14} strokeWidth={2} aria-hidden="true" />
        </Button>

        <Button
          type="button"
          variant="outline"
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={handleNext}
          aria-label="Next page"
          title="Next page"
          className="
            !size-8
            !min-h-0
            !min-w-0
            !rounded-full
            !p-0
            flex
            shrink-0
            items-center
            justify-center
            border-border-primary
            bg-background-secondary
            text-text-secondary
            shadow-none
            transition-colors
            hover:bg-background-tertiary
            hover:text-text-primary
            disabled:pointer-events-none
            disabled:opacity-35
          "
        >
          <ChevronRight size={14} strokeWidth={2} aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
};

/**
 * Functional component for pagination
 * @param currentPage: Current Page Number
 * @param totalItems: Total number of items to paginate
 * @param pageSize: Number of items per page(Default: 10)
 * @param onPageChange: Callback function when a page is selected
 * @param maxPageButtons: Maximum number of page buttons to display (Default: 5)
 * @param label: label of given pagination
 */
export const PaginationNew = ({
  currentPage,
  totalItems,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  label = "",
  className = "",
  hasNext = false,
  hasPrev = false,
}) => {
  const handlePrev = () => {
    if (!hasPrev) return;

    onPageChange(currentPage - 1);
  };

  const handleNext = () => {
    if (!hasNext) return;

    onPageChange(currentPage + 1);
  };

  const start = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;

  const end = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className={cn("flex items-center justify-between gap-4 rounded-b-xl border-t border-border-primary bg-background-secondary px-3 py-2.5", className)}>
      {/* Items per page */}
      <div className="flex shrink-0 items-center gap-2 text-xs text-text-secondary">
        <span>Items per page</span>

        {/* The app's `Select`, not a raw <select>: the native control
            draws its own list from the operating system, which stayed
            a white popup with black rows under the dark theme. The
            width lives on the wrapper — `Select` fills its box. */}
        <div className="w-[72px]">
          <Select value={pageSize} onChange={onPageSizeChange} options={PAGE_SIZES} className="h-7 px-2 text-xs font-normal" />
        </div>
      </div>

      {/* Count + navigation */}
      <div className="flex items-center gap-3">
        {/* Showing text */}
        <span className="hidden text-[12px] text-text-secondary md:inline">{totalItems === 0 ? `No ${label}` : `Showing ${start}-${end} of ${totalItems} ${label}`}</span>

        {/* Navigation buttons */}
        <div className="flex items-center gap-1.5">
          <Button
            type="button"
            variant="outline"
            disabled={!hasPrev}
            onClick={handlePrev}
            aria-label="Previous page"
            title="Previous page"
            className="
              !size-8
              !min-h-0
              !min-w-0
              !rounded-full
              !p-0
              flex
              shrink-0
              items-center
              justify-center
              border-border-primary
              bg-background-secondary
              text-text-secondary
              shadow-none
              transition-colors
              hover:bg-background-tertiary
              hover:text-text-primary
              disabled:pointer-events-none
              disabled:opacity-35
            "
          >
            <ChevronLeft size={14} strokeWidth={2} aria-hidden="true" />
          </Button>

          <Button
            type="button"
            variant="outline"
            disabled={!hasNext}
            onClick={handleNext}
            aria-label="Next page"
            title="Next page"
            className="
              !size-8
              !min-h-0
              !min-w-0
              !rounded-full
              !p-0
              flex
              shrink-0
              items-center
              justify-center
              border-border-primary
              bg-background-secondary
              text-text-secondary
              shadow-none
              transition-colors
              hover:bg-background-tertiary
              hover:text-text-primary
              disabled:pointer-events-none
              disabled:opacity-35
            "
          >
            <ChevronRight size={14} strokeWidth={2} aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
